import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Divider,
    FormControlLabel,
    Popover,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import IconButton from '../ui/IconButton';
import Tooltip from '../ui/Tooltip';
import { HeaderTitle } from './grid/WatchlistGridHeader';

// The Watchlist's four trailing-return columns. PMs read them together as a
// trend, so one basis (currency and dividends) applies to all four. The column
// ids are the ones users' saved sort and column choices already refer to.
export const TRAILING_RETURN_WINDOWS = Object.freeze([
    { key: 'mtd', columnId: 'Return MTD' },
    { key: '1m', columnId: 'Return 1m' },
    { key: '3m', columnId: 'Return 3m' },
    { key: '6m', columnId: 'Return 6m' },
]);

const CURRENCY_OPTIONS = [
    { value: 'usd', label: 'USD', exportLabel: 'USD' },
    { value: 'local', label: 'Local', exportLabel: 'local currency' },
];
const DIVIDEND_OPTIONS = [
    { value: 'total', label: 'Included', basisLabel: 'with dividends' },
    { value: 'price', label: 'Price only', basisLabel: 'price only' },
];
// A basis is `${currency}_${dividends}`, the suffix of each return field.
const RETURNS_BASES = Object.freeze(CURRENCY_OPTIONS.flatMap((currency) => (
    DIVIDEND_OPTIONS.map((dividends) => `${currency.value}_${dividends.value}`)
)));
const DEFAULT_RETURNS_BASIS = 'usd_total';
export const RETURNS_BASIS_STORAGE_KEY = 'watchlist-returns-basis';

function parseReturnsBasis(basis) {
    const [currency, dividends] = String(basis).split('_');
    return {
        currency: CURRENCY_OPTIONS.find((option) => option.value === currency) || CURRENCY_OPTIONS[0],
        dividends: DIVIDEND_OPTIONS.find((option) => option.value === dividends) || DIVIDEND_OPTIONS[0],
    };
}

/** The row field a return window reads for a basis, e.g. return_3m_usd_total. */
export function returnsFieldName(windowKey, basis) {
    return `return_${windowKey}_${basis}`;
}

/** The short label the return headers show, e.g. "USD · with dividends". */
function returnsBasisLabel(basis) {
    const { currency, dividends } = parseReturnsBasis(basis);
    return `${currency.label} · ${dividends.basisLabel}`;
}

/** The browser's remembered basis; the default without storage. */
export function readPersistedReturnsBasis() {
    try {
        const stored = window.localStorage.getItem(RETURNS_BASIS_STORAGE_KEY);
        return RETURNS_BASES.includes(stored) ? stored : DEFAULT_RETURNS_BASIS;
    } catch {
        return DEFAULT_RETURNS_BASIS;
    }
}

export function persistReturnsBasis(basis) {
    try {
        window.localStorage.setItem(RETURNS_BASIS_STORAGE_KEY, basis);
    } catch {
        // Without storage the choice lasts until the page reloads.
    }
}

const TRAILING_RETURN_FIELDS = new Set(TRAILING_RETURN_WINDOWS.flatMap(({ key }) => (
    RETURNS_BASES.map((basis) => returnsFieldName(key, basis))
)));

/**
 * A Watchlist download row's return columns: the four windows of the shown
 * basis, named for it ("Return 3m (USD, with dividends)"), in place of the
 * row's return fields for every basis.
 */
export function withReturnsExportColumns(row, basis) {
    const { currency, dividends } = parseReturnsBasis(basis);
    const otherFields = Object.fromEntries(
        Object.entries(row).filter(([field]) => !TRAILING_RETURN_FIELDS.has(field)),
    );
    return {
        ...otherFields,
        ...Object.fromEntries(TRAILING_RETURN_WINDOWS.map(({ key, columnId }) => [
            `${columnId} (${currency.exportLabel}, ${dividends.basisLabel})`,
            row[returnsFieldName(key, basis)] ?? null,
        ])),
    };
}

// The basis under the window: small, regular, secondary text.
const BASIS_LINE_CLASS_NAME = 'overflow-hidden text-ellipsis whitespace-nowrap text-[0.68rem] font-normal '
    + 'leading-[1.25] text-content-secondary';

/**
 * Header content of a return column: its window and the shown basis. The
 * first visible return column also holds the basis menu (`selectorId`).
 */
export function WatchlistReturnsColumnHeader({
    label,
    basis,
    selectorId = null,
    anchorEl = null,
    onOpen = null,
}) {
    const basisLabel = returnsBasisLabel(basis);
    return (
        <div className="flex min-w-0 items-center gap-0.5">
            <div className="flex min-w-0 flex-col">
                <HeaderTitle label={label} />
                <span className={BASIS_LINE_CLASS_NAME}>{basisLabel}</span>
            </div>
            {selectorId && (
                <Tooltip content="Choose the currency and dividends for all four return columns">
                    <IconButton
                        size="xs"
                        label={`Choose return basis; currently ${basisLabel}`}
                        onClick={(event) => {
                            // The header sorts on click; this button only opens the menu.
                            event.stopPropagation();
                            onOpen(event.currentTarget);
                        }}
                        aria-haspopup="dialog"
                        aria-controls={anchorEl ? selectorId : undefined}
                        aria-expanded={Boolean(anchorEl)}
                        // In the header's text colour, as the identity picker.
                        className="!text-inherit"
                    >
                        <ChevronDown size={14} strokeWidth={2.5} aria-hidden="true" />
                    </IconButton>
                </Tooltip>
            )}
        </div>
    );
}

WatchlistReturnsColumnHeader.propTypes = {
    label: PropTypes.string.isRequired,
    basis: PropTypes.string.isRequired,
    selectorId: PropTypes.string,
    anchorEl: PropTypes.object,
    onOpen: PropTypes.func,
};

function BasisChoice({ label, value, options, onChange }) {
    return (
        <Box sx={{ px: 0.75, pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                {label}
            </Typography>
            <RadioGroup
                row
                aria-label={label}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            >
                {options.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size="small" />}
                        label={option.label}
                        sx={{ ml: 0, mr: 1.5, '& .MuiFormControlLabel-label': { fontSize: '0.82rem' } }}
                    />
                ))}
            </RadioGroup>
        </Box>
    );
}

BasisChoice.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
    onChange: PropTypes.func.isRequired,
};

/**
 * The return columns' basis menu. It stays open while both choices are made;
 * each choice applies to the four columns at once.
 */
export default function WatchlistReturnsBasisSelector({
    anchorEl,
    selectorId,
    basis,
    onChange,
    onClose,
    paperSx = null,
}) {
    const { currency, dividends } = parseReturnsBasis(basis);
    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
                id: selectorId,
                role: 'dialog',
                'aria-labelledby': `${selectorId}-title`,
                sx: paperSx ? [paperSx, { width: 250, p: 1 }] : { width: 250, p: 1 },
            }}
        >
            <Typography
                id={`${selectorId}-title`}
                variant="subtitle2"
                sx={{ fontWeight: 700, px: 0.75, py: 0.5 }}
            >
                Show returns
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', px: 0.75, pb: 0.75 }}
            >
                All four return columns use this choice.
            </Typography>
            <Divider sx={{ mb: 0.25 }} />
            <BasisChoice
                label="Currency"
                value={currency.value}
                options={CURRENCY_OPTIONS}
                onChange={(value) => onChange(`${value}_${dividends.value}`)}
            />
            <BasisChoice
                label="Dividends"
                value={dividends.value}
                options={DIVIDEND_OPTIONS}
                onChange={(value) => onChange(`${currency.value}_${value}`)}
            />
        </Popover>
    );
}

WatchlistReturnsBasisSelector.propTypes = {
    anchorEl: PropTypes.object,
    selectorId: PropTypes.string.isRequired,
    basis: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    paperSx: PropTypes.object,
};
